// FieldDesk — универсальная модалка «Новый выезд».
// Two-mode: «Для сделки» / «Адрес вручную». Тип DUTY удалён в миграции
// 20260502120000_remove_visit_type_duty.

import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import L from 'leaflet';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import type { CreateVisitInput, DealSearchItem, EngineerListItem, VisitType } from '../api/types';
import { TYPE_COLOR, TYPE_LABEL } from '../styles/theme';

const BISHKEK_CENTER: [number, number] = [42.8746, 74.5698];

export interface NewTaskPrefill {
  bitrixDealId?: string | null;
  clientId?: number | null;
  clientLabel?: string | null;
}

interface Props {
  onClose(): void;
  onCreated(): void;
  prefill?: NewTaskPrefill | null;
  /** В embedded-mode создавать визит без bitrixDealId нельзя. */
  allowAdHoc?: boolean;
}

const TYPES: VisitType[] = ['INSTALLATION', 'TRAINING', 'PAID_VISIT', 'ONLINE'];

function defaultDate(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function NewTaskModal({ onClose, onCreated, prefill, allowAdHoc = true }: Props) {
  const { api, state: authState } = useAuth();
  const currentUserId = authState.status === 'authenticated' ? authState.user.id : null;
  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [form, setForm] = useState({
    engineerId: 0,
    type: 'INSTALLATION' as VisitType,
    scheduledDate: defaultDate(),
    scheduledStart: '09:00',
    scheduledEnd: '12:00',
    description: '',
    clientId: prefill?.clientId ?? 0,
    bitrixDealId: prefill?.bitrixDealId ?? '',
    // Sprint UX-fix: ad-hoc location для выездов без клиента/сделки.
    // Пусто — backend получит null/undefined.
    customAddress: '',
    customLat: '' as string, // строка для контролируемого input; парсится при submit
    customLng: '' as string,
  });

  // Two-mode: 'deal' (выезд привязан к сделке Б24) | 'manual' (произвольный
  // адрес). По умолчанию: если в prefill есть dealId или мы в embedded —
  // 'deal'. Иначе — 'manual'.
  const dealLocked = !!prefill?.bitrixDealId; // нельзя сменить сделку (приехали из карточки)
  const [mode, setMode] = useState<'deal' | 'manual'>(
    prefill?.bitrixDealId || !allowAdHoc ? 'deal' : 'manual',
  );

  const adHocBlocked = !allowAdHoc && mode === 'manual';
  const showCustomLocation = mode === 'manual';

  // Жёлтое предупреждение — только в locked-deal режиме когда у сделки нет
  // привязанного клиента (ни компании, ни контакта не нашли на бэкенде).
  // Не блокирует создание: выезд создастся с привязкой к сделке без clientId.
  const dealNoCompanyWarning = dealLocked && mode === 'deal' && !prefill?.clientLabel;

  useEffect(() => {
    api
      .listEngineers()
      .then((list) => {
        setEngineers(list);
        if (list.length > 0 && form.engineerId === 0) {
          // Если выезд создаёт сам инженер — по умолчанию подставляем его,
          // чтобы по невнимательности не назначали другого. Диспетчеров в
          // списке инженеров нет — им по-прежнему первый из списка.
          const self = list.find((e) => e.id === currentUserId);
          setForm((f) => ({ ...f, engineerId: (self ?? list[0]!).id }));
        }
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const canSubmit = useMemo(() => {
    if (submitting || loading) return false;
    if (!form.engineerId) return false;
    if (adHocBlocked) return false;
    if (mode === 'deal' && !form.bitrixDealId && !form.clientId) return false;
    // В manual-режиме требуем хотя бы адрес или координаты.
    if (mode === 'manual' && !form.customAddress.trim() && !form.customLat.trim()) return false;
    if (form.scheduledStart >= form.scheduledEnd) return false;
    return true;
  }, [form, submitting, loading, adHocBlocked, mode]);

  async function handleSubmit() {
    setErr(null);
    setSubmitting(true);
    try {
      const body: CreateVisitInput = {
        engineerId: form.engineerId,
        type: form.type,
        scheduledDate: form.scheduledDate,
        scheduledStart: form.scheduledStart,
        scheduledEnd: form.scheduledEnd,
        description: form.description || undefined,
      };
      {
        if (mode === 'deal') {
          if (form.clientId) body.clientId = form.clientId;
          if (form.bitrixDealId) body.bitrixDealId = form.bitrixDealId;
        } else if (mode === 'manual') {
          if (form.customAddress.trim()) body.customAddress = form.customAddress.trim();
          const lat = Number(form.customLat);
          const lng = Number(form.customLng);
          if (form.customLat.trim() && Number.isFinite(lat)) body.customLat = lat;
          if (form.customLng.trim() && Number.isFinite(lng)) body.customLng = lng;
        }
      }
      await api.createVisit(body);
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 9999 — выше Leaflet-панелей (zoom-control 1000, popup 700, tile 200).
        // На экране Карты при zIndex<1000 модалка уезжала ПОД карту.
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 520,
          maxWidth: '94vw',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 18px 60px rgba(15,23,42,0.25)',
        }}
      >
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700 }}>Новый выезд</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8' }}>
              ✕
            </button>
          </div>
          {prefill?.clientLabel && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Клиент из сделки: <b style={{ color: '#0f172a' }}>{prefill.clientLabel}</b>
            </div>
          )}
        </div>

        <div style={{ padding: 24, display: 'grid', gap: 14 }}>
          {err && (
            <div
              style={{
                padding: '10px 12px',
                background: '#fef2f2',
                color: '#b91c1c',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          )}

          <Field label="Тип выезда">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setForm((f) => ({ ...f, type: t }))}
                  style={{
                    padding: '7px 12px',
                    borderRadius: 8,
                    border: form.type === t ? `2px solid ${TYPE_COLOR[t]}` : '1px solid #e2e8f0',
                    background: form.type === t ? TYPE_COLOR[t] + '14' : '#fff',
                    color: form.type === t ? TYPE_COLOR[t] : '#475569',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Инженер">
            <select
              value={form.engineerId}
              onChange={(e) => setForm((f) => ({ ...f, engineerId: Number(e.target.value) }))}
              style={selectStyle}
            >
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.fullName} {e.status === 'busy' ? '· занят' : ''}
                </option>
              ))}
            </select>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 150px', gap: 10 }}>
            <Field label="Дата">
              <input
                type="date"
                value={form.scheduledDate}
                onChange={(e) => setForm((f) => ({ ...f, scheduledDate: e.target.value }))}
                style={inputStyle}
              />
            </Field>
            <Field label="Быть у клиента к">
              <input
                type="time"
                value={form.scheduledStart}
                onChange={(e) => setForm((f) => ({ ...f, scheduledStart: e.target.value }))}
                style={inputStyle}
              />
            </Field>
            <Field label="Закончить работу к">
              <input
                type="time"
                value={form.scheduledEnd}
                onChange={(e) => setForm((f) => ({ ...f, scheduledEnd: e.target.value }))}
                style={inputStyle}
              />
            </Field>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: -6, marginBottom: 12, lineHeight: 1.4 }}>
            Время работы у клиента, без дороги. Опоздание считается от «Быть у клиента к» (допуск 10 мин).
          </div>

          <Field label="Комментарий">
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Что нужно сделать на объекте"
            />
          </Field>

          <ModeTabs mode={mode} onChange={setMode} disabled={dealLocked || !allowAdHoc ? mode : null} />

          {mode === 'deal' && (
            <DealPicker
              api={api}
              locked={dealLocked}
              prefillLabel={prefill?.clientLabel ?? null}
              dealId={form.bitrixDealId}
              onPick={(d) =>
                setForm((f) => ({
                  ...f,
                  bitrixDealId: d?.id ?? '',
                  // При смене сделки сбрасываем clientId — он подхватится
                  // на бэкенде через syncClientFromDeal при следующем шаге.
                  clientId: 0,
                }))
              }
            />
          )}

          {showCustomLocation && (
            <ManualAddressBlock
              api={api}
              address={form.customAddress}
              lat={form.customLat}
              lng={form.customLng}
              onAddressChange={(v) => setForm((f) => ({ ...f, customAddress: v }))}
              onLatChange={(v) => setForm((f) => ({ ...f, customLat: v }))}
              onLngChange={(v) => setForm((f) => ({ ...f, customLng: v }))}
            />
          )}

          {dealNoCompanyWarning && (
            <div
              style={{
                fontSize: 12,
                color: '#92400e',
                background: '#fefce8',
                border: '1px solid #fde68a',
                padding: '8px 10px',
                borderRadius: 8,
                lineHeight: 1.4,
              }}
            >
              ⚠ У сделки нет привязанной компании или контакта. Выезд будет создан с привязкой к
              сделке — клиент останется пустым. При необходимости добавьте компанию в Битрикс24.
            </div>
          )}

          {adHocBlocked && (
            <div
              style={{
                fontSize: 12,
                color: '#b91c1c',
                background: '#fef2f2',
                padding: '8px 10px',
                borderRadius: 8,
              }}
            >
              Ad-hoc выезды без привязки к сделке доступны только в standalone-режиме.
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '9px 16px',
              background: '#f1f5f9',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              color: '#475569',
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              padding: '9px 18px',
              background: canSubmit ? '#2563eb' : '#cbd5e1',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {submitting ? 'Создаём…' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 13,
  background: '#fff',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
};

// --- Tabs «Для сделки» / «Адрес вручную» -----------------------------------
function ModeTabs({
  mode,
  onChange,
  disabled,
}: {
  mode: 'deal' | 'manual';
  onChange(m: 'deal' | 'manual'): void;
  /** Если задан — это режим, к которому пользователь принудительно прикреплён
   *  (нельзя переключиться). Например, в embedded+deal_context всегда 'deal'. */
  disabled: 'deal' | 'manual' | null;
}) {
  const items: Array<{ key: 'deal' | 'manual'; label: string; hint: string }> = [
    { key: 'deal', label: 'Для сделки', hint: 'Привязка к Bitrix сделке' },
    { key: 'manual', label: 'Адрес вручную', hint: 'Свободный адрес и координаты' },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 6,
        background: '#f1f5f9',
        padding: 4,
        borderRadius: 10,
      }}
    >
      {items.map((it) => {
        const active = mode === it.key;
        const lockedAway = disabled !== null && disabled !== it.key;
        return (
          <button
            key={it.key}
            onClick={() => !lockedAway && onChange(it.key)}
            disabled={lockedAway}
            style={{
              padding: '8px 10px',
              border: 'none',
              borderRadius: 8,
              background: active ? '#fff' : 'transparent',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              color: active ? '#0f172a' : lockedAway ? '#cbd5e1' : '#64748b',
              fontWeight: 600,
              fontSize: 12,
              cursor: lockedAway ? 'not-allowed' : 'pointer',
              textAlign: 'center',
            }}
            title={it.hint}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// --- Combobox по сделкам Б24 -----------------------------------------------
interface DealPickerApi {
  searchBitrixDeals(q: string, limit?: number): Promise<{ items: DealSearchItem[] }>;
}

function DealPicker({
  api,
  locked,
  prefillLabel,
  dealId,
  onPick,
}: {
  api: DealPickerApi;
  locked: boolean;
  prefillLabel: string | null;
  dealId: string;
  onPick(d: DealSearchItem | null): void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DealSearchItem[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [pickedTitle, setPickedTitle] = useState<string | null>(null);

  // Debounced search.
  useEffect(() => {
    if (locked) return;
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.searchBitrixDeals(query.trim(), 20);
        setResults(r.items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, api, locked]);

  if (locked) {
    return (
      <div
        style={{
          fontSize: 12,
          color: '#475569',
          background: '#eff6ff',
          padding: '10px 12px',
          borderRadius: 8,
          border: '1px solid #bfdbfe',
        }}
      >
        🔗 Сделка Б24 #{dealId}
        {prefillLabel ? ` — ${prefillLabel}` : ''}. Визит появится в timeline сделки.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Field label="Сделка Bitrix24">
        <input
          type="text"
          value={dealId && pickedTitle ? `#${dealId} — ${pickedTitle}` : query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (dealId) {
              onPick(null);
              setPickedTitle(null);
            }
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Введите часть названия сделки…"
          style={inputStyle}
        />
      </Field>
      {open && query.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            zIndex: 10,
            maxHeight: 240,
            overflowY: 'auto',
            marginTop: 4,
          }}
        >
          {searching && <div style={{ padding: 10, fontSize: 12, color: '#94a3b8' }}>Поиск…</div>}
          {!searching && results.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: '#94a3b8' }}>Ничего не найдено</div>
          )}
          {!searching &&
            results.map((d) => (
              <button
                key={d.id}
                onClick={() => {
                  onPick(d);
                  setPickedTitle(d.title);
                  setQuery('');
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: '1px solid #f1f5f9',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <div style={{ color: '#0f172a', fontWeight: 600 }}>{d.title}</div>
                <div style={{ color: '#94a3b8', fontSize: 10 }}>#{d.id}</div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// --- Manual address block с интерактивной mini-Leaflet картой -------------
interface ManualBlockApi {
  geocodeReverse(lat: number, lng: number): Promise<{ ok: boolean; address: string | null }>;
  geocodeForward(q: string): Promise<{ ok: boolean; lat: number | null; lng: number | null }>;
}

function ManualAddressBlock({
  api,
  address,
  lat,
  lng,
  onAddressChange,
  onLatChange,
  onLngChange,
}: {
  api: ManualBlockApi;
  address: string;
  lat: string;
  lng: string;
  onAddressChange(v: string): void;
  onLatChange(v: string): void;
  onLngChange(v: string): void;
}) {
  const [busy, setBusy] = useState<'reverse' | 'forward' | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // Refs для Leaflet — карта живёт вне React virtual DOM, через imperative API.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  // anti-loop: при reverse-geocode мы подставляем address, но это НЕ должно
  // тригерить повторный forward-цикл. lastReverseRef хранит ключ
  // последней успешной обработки.
  const lastReverseRef = useRef<string | null>(null);

  // Reverse-геокод по точке. Используется и при click, и при drag.
  async function applyPickedPoint(latNum: number, lngNum: number) {
    onLatChange(latNum.toFixed(6));
    onLngChange(lngNum.toFixed(6));
    const key = `${latNum.toFixed(5)},${lngNum.toFixed(5)}`;
    if (lastReverseRef.current === key) return;
    lastReverseRef.current = key;
    setBusy('reverse');
    setHint(null);
    try {
      const r = await api.geocodeReverse(latNum, lngNum);
      if (r.ok && r.address) {
        onAddressChange(r.address);
        setHint('✓ Адрес подставлен из точки на карте.');
      } else {
        setHint('Адрес по координатам не найден — задайте вручную.');
      }
    } catch (e) {
      setHint(`Ошибка: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  function ensureMarker(latNum: number, lngNum: number): L.Marker {
    const map = mapRef.current!;
    if (markerRef.current) {
      markerRef.current.setLatLng([latNum, lngNum]);
      return markerRef.current;
    }
    const m = L.marker([latNum, lngNum], { draggable: true });
    m.on('dragend', () => {
      const ll = m.getLatLng();
      void applyPickedPoint(ll.lat, ll.lng);
    });
    m.addTo(map);
    markerRef.current = m;
    return m;
  }

  // Init Leaflet один раз. При unmount — корректно cleanup.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    // Если уже введены координаты — центрируем на них; иначе Бишкек.
    const initLat = Number(lat);
    const initLng = Number(lng);
    const center: [number, number] =
      Number.isFinite(initLat) && Number.isFinite(initLng) && lat.trim() && lng.trim()
        ? [initLat, initLng]
        : BISHKEK_CENTER;
    const map = L.map(containerRef.current).setView(center, 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    map.on('click', (ev: L.LeafletMouseEvent) => {
      const { lat: clickLat, lng: clickLng } = ev.latlng;
      ensureMarker(clickLat, clickLng);
      void applyPickedPoint(clickLat, clickLng);
    });
    if (center !== BISHKEK_CENTER) {
      ensureMarker(center[0], center[1]);
    }
    mapRef.current = map;
    // Leaflet считает контейнер размер при mount. Если модалка ещё в
    // animation/layout — ждём кадр и invalidateSize. Без этого тайлы
    // остаются «в углу».
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Forward — вводят адрес и нажимают «Найти на карте»: геокодим, центрируем
  // карту, ставим маркер.
  async function handleForward() {
    if (!address.trim()) return;
    setBusy('forward');
    setHint(null);
    try {
      const r = await api.geocodeForward(address.trim());
      if (r.ok && r.lat != null && r.lng != null) {
        onLatChange(r.lat.toFixed(6));
        onLngChange(r.lng.toFixed(6));
        const map = mapRef.current;
        if (map) {
          map.setView([r.lat, r.lng], 16);
          ensureMarker(r.lat, r.lng);
          // anti-loop: эта точка теперь «совпадает» с адресом — отметим,
          // чтобы reverse не перетёр введённый адрес обратно.
          lastReverseRef.current = `${r.lat.toFixed(5)},${r.lng.toFixed(5)}`;
        }
        setHint(`✓ Найдено: ${r.lat.toFixed(5)}, ${r.lng.toFixed(5)}`);
      } else {
        setHint('По адресу ничего не найдено — кликните на карту, чтобы поставить точку.');
      }
    } catch (e) {
      setHint(`Ошибка: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        background: '#f8fafc',
        padding: '12px 14px',
        borderRadius: 8,
        border: '1px dashed #cbd5e1',
        display: 'grid',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
        Адрес вручную — кликните по карте или введите адрес
      </div>

      <Field label="Адрес">
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="напр. ул. Чуй 168, Бишкек"
            style={inputStyle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleForward();
              }
            }}
          />
          <button
            type="button"
            onClick={handleForward}
            disabled={busy !== null || !address.trim()}
            style={{
              padding: '8px 14px',
              border: '1px solid #2563eb',
              background: busy === 'forward' ? '#dbeafe' : '#2563eb',
              color: busy === 'forward' ? '#1d4ed8' : '#fff',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {busy === 'forward' ? 'Ищем…' : 'Найти на карте'}
          </button>
        </div>
      </Field>

      {/* Mini-Leaflet, ~400×280. Click → reverse, drag маркера → reverse. */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: 280,
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          // z-index ниже модалки (модалка 9999), но Leaflet pane'ы внутри
          // контейнера — собственная stacking context, не вылезают.
        }}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="Широта (lat)">
          <input
            type="text"
            inputMode="decimal"
            value={lat}
            readOnly
            placeholder="—"
            style={{ ...inputStyle, background: '#f1f5f9', color: '#475569' }}
          />
        </Field>
        <Field label="Долгота (lng)">
          <input
            type="text"
            inputMode="decimal"
            value={lng}
            readOnly
            placeholder="—"
            style={{ ...inputStyle, background: '#f1f5f9', color: '#475569' }}
          />
        </Field>
      </div>

      {hint && (
        <div style={{ fontSize: 11, color: hint.startsWith('✓') ? '#16a34a' : '#b45309' }}>{hint}</div>
      )}
      <div style={{ fontSize: 10, color: '#94a3b8' }}>
        Кликните по карте, чтобы поставить точку. Маркер можно перетащить.
      </div>
    </div>
  );
}
